import { useMemo, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material'
import { CircleAlert, Download, Pencil } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyState, Eyebrow, Segmented, Skeleton, Surface, formatDate, ha, num, saveAs, tokens,
} from '@agribot/polygon-checks-ui'
import {
  type BillingPeriodQuery, useAdminOrganisations, useAllBillingUsage, useBillingUsage, useSetSubscription,
} from './hooks'
import type { BillingUsage, Subscription } from './types'
import { allCsv, oneCsv } from './billing-csv'

/**
 * What each organisation's subscription bills against: suppliers in scope of
 * its campaigns, unique hectares verified (each plot once a period, however
 * often it is checked or uploaded again), and seats, against the allowances
 * Agribot sets. Platform admins only; the figures and how each is measured
 * are in docs/BILLING-USAGE.md (eudr-api).
 */
export function Billing() {
  const [params, setParams] = useSearchParams()
  const org = params.get('org') ?? 'all'
  const periodKind = (params.get('period') ?? 'year') as 'year' | 'month' | 'custom'
  const month = params.get('month') ?? new Date().toISOString().slice(0, 7)
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const set = (changes: Record<string, string | null>) =>
    setParams((p) => {
      for (const [k, v] of Object.entries(changes)) {
        if (v === null) p.delete(k)
        else p.set(k, v)
      }
      return p
    }, { replace: true })

  const period: BillingPeriodQuery = useMemo(() => {
    if (periodKind === 'month') return { month }
    if (periodKind === 'custom' && from && to) {
      return { from: new Date(`${from}T00:00:00Z`).toISOString(), to: new Date(`${to}T00:00:00Z`).toISOString() }
    }
    return {}
  }, [periodKind, month, from, to])

  const { data: organisations } = useAdminOrganisations()
  const one = useBillingUsage(org === 'all' ? null : org, period)
  const all = useAllBillingUsage(period, org === 'all')
  const [editing, setEditing] = useState(false)

  const download = () => {
    if (org === 'all' && all.data) saveAs(allCsv(all.data), `billing-usage-all.csv`)
    if (org !== 'all' && one.data) saveAs(oneCsv(one.data), `billing-usage-${slug(one.data.organisation)}.csv`)
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 5, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Organisation" value={org} sx={{ minWidth: 240 }}
          onChange={(e) => set({ org: e.target.value })}
        >
          <MenuItem value="all">All organisations</MenuItem>
          {(organisations ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
        </TextField>
        <Segmented
          ariaLabel="Period"
          value={periodKind}
          onChange={(v) => set({ period: v })}
          options={[
            { value: 'year', label: 'Subscription year' },
            { value: 'month', label: 'Month' },
            { value: 'custom', label: 'Custom' },
          ]}
        />
        {periodKind === 'month' && (
          <TextField
            size="small" type="month" label="Month" value={month} InputLabelProps={{ shrink: true }}
            onChange={(e) => e.target.value && set({ month: e.target.value })}
          />
        )}
        {periodKind === 'custom' && (
          <>
            <TextField size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }} onChange={(e) => set({ from: e.target.value })} />
            <TextField size="small" type="date" label="To" value={to} InputLabelProps={{ shrink: true }} onChange={(e) => set({ to: e.target.value })} />
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {org !== 'all' && (
          <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={() => setEditing(true)} disabled={!one.data}>
            Subscription
          </Button>
        )}
        <Button variant="outlined" startIcon={<Download size={16} />} onClick={download}
          disabled={org === 'all' ? !all.data : !one.data}>
          CSV
        </Button>
      </Box>

      {org === 'all'
        ? <AllOrganisations items={all.data} loading={all.isLoading} onOpen={(id) => set({ org: id })} />
        : <OneOrganisation usage={one.data} loading={one.isLoading} />}

      {editing && one.data && (
        <SubscriptionDialog usage={one.data} onClose={() => setEditing(false)} />
      )}
    </>
  )
}

// ---------------------------------------------------------------- all organisations

const ALL_COLUMNS = [
  { label: 'Organisation', width: 'minmax(200px,1.4fr)' },
  { label: 'Tier', width: 'minmax(120px,0.8fr)' },
  { label: 'Suppliers in scope', width: '150px', right: true },
  { label: 'Unique hectares', width: '150px', right: true },
  { label: 'Seats', width: '110px', right: true },
  { label: 'Active suppliers', width: '130px', right: true },
]

function AllOrganisations({ items, loading, onOpen }: { items?: BillingUsage[]; loading: boolean; onOpen: (id: string) => void }) {
  const grid = ALL_COLUMNS.map((c) => c.width).join(' ')
  const rows = (items ?? []).filter((i) =>
    i.suppliers.inScope || i.hectares.uniqueVerified || i.users.seats || i.subscription)
  return (
    <Surface sx={{ overflow: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: grid, columnGap: 3, px: 5, minWidth: 860 }}>
        {ALL_COLUMNS.map((c) => <HeaderCell key={c.label} right={c.right}>{c.label}</HeaderCell>)}
        {loading && Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ gridColumn: '1 / -1', py: 1.5 }}><Skeleton height={28} /></Box>
        ))}
        {rows.map((u) => (
          <Box
            key={u.organisationId} role="button" tabIndex={0}
            onClick={() => onOpen(u.organisationId)}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(u.organisationId) }}
            sx={{ display: 'contents', cursor: 'pointer', '&:hover > *': { backgroundColor: tokens.color.ink50 } }}
          >
            <Cell><Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{u.organisation}</Typography></Cell>
            <Cell><Typography variant="body2" sx={{ color: u.subscription?.tier ? 'text.primary' : 'text.disabled' }}>{u.subscription?.tier ?? 'Not set'}</Typography></Cell>
            <Cell right><Against value={num(u.suppliers.inScope)} allowance={u.suppliers.allowance} pct={u.suppliers.usedPct} /></Cell>
            <Cell right><Against value={ha(u.hectares.uniqueVerified, 0)} allowance={u.hectares.allowance} pct={u.hectares.usedPct} /></Cell>
            <Cell right><Against value={num(u.users.seats)} allowance={u.users.allowance} pct={u.users.usedPct} /></Cell>
            <Cell right><Typography variant="body2">{num(u.users.activeSupplierCompanies)}</Typography></Cell>
          </Box>
        ))}
      </Box>
      {!loading && rows.length === 0 && (
        <EmptyState title="Nothing billable yet" body="No organisation has suppliers in scope, verified hectares or seats in this period." />
      )}
    </Surface>
  )
}

function Against({ value, allowance, pct }: { value: string; allowance: number | null; pct: number | null }) {
  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {allowance != null && (
        <Typography variant="caption" sx={{ color: pct != null && pct > 100 ? tokens.color.warning : 'text.disabled' }}>
          {pct}% of {num(allowance)}
        </Typography>
      )}
    </Box>
  )
}

// ---------------------------------------------------------------- one organisation

function OneOrganisation({ usage, loading }: { usage?: BillingUsage; loading: boolean }) {
  if (loading || !usage) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 3 }}>
        {[0, 1, 2].map((i) => <Surface key={i} sx={{ p: 5 }}><Skeleton width={140} height={30} /></Surface>)}
      </Box>
    )
  }
  const { suppliers, hectares, users, activity, subscription, period } = usage
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h3" component="h2">{usage.organisation}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {subscription?.tier ?? 'No subscription set'} · {period.label}
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 3, mb: 6 }}>
        <Tile
          label="Suppliers in scope"
          value={num(suppliers.inScope)}
          note={`Legal entities in ${num(suppliers.campaignsOpen)} campaign${suppliers.campaignsOpen === 1 ? '' : 's'} open in the period`}
          allowance={suppliers.allowance} pct={suppliers.usedPct} unit=""
        />
        <Tile
          label="Unique hectares verified"
          value={ha(hectares.uniqueVerified, 0)}
          note={`${num(hectares.uniquePlots)} plots, each counted once however often checked`}
          allowance={hectares.allowance} pct={hectares.usedPct} unit=" ha"
        />
        <Tile
          label="Seats"
          value={num(users.seats)}
          note="Staff holding a role today"
          allowance={users.allowance} pct={users.usedPct} unit=""
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0,1fr))' }, gap: 3, mb: 6 }}>
        <Section title="Hectares" rows={[
          ['Plot checks', num(hectares.plotChecks)],
          ['Check runs', num(hectares.checkRuns)],
          ['Uploads', `${num(hectares.uploads)} · ${ha(hectares.uploadedHa, 0)}`],
          ['Stored now', `${num(hectares.storedPlots)} plots · ${ha(hectares.storedHa, 0)}`],
        ]} />
        <Section title="Users" rows={[
          ['Active staff', num(users.activeStaff)],
          ['Supplier logins', num(users.supplierLogins)],
          ['Active supplier logins', num(users.activeSupplierLogins)],
          ['Active suppliers', num(users.activeSupplierCompanies)],
          ['Parent groups in scope', num(suppliers.parentGroups)],
          ['Materials in scope', num(suppliers.materials)],
        ]} />
        <Section title="Activity" rows={[
          ['Campaigns opened', num(activity.campaignsOpened)],
          ['Submissions submitted', num(activity.submissionsSubmitted)],
          ['Submissions approved', num(activity.submissionsApproved)],
          ['Exports', num(activity.exports)],
          ['Scenario runs', num(activity.scenarioRuns)],
          ['Emails sent', num(activity.emailsSent)],
          ['API requests (user tokens)', num(activity.tokenRequests)],
          ['Sourcing API requests', num(activity.sourcingApiRequests)],
          ['Document storage now', `${activity.documentStorageGb.toLocaleString('en-GB', { maximumFractionDigits: 3 })} GB`],
        ]} />
      </Box>

      {usage.monthly && usage.monthly.length > 1 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0,1fr))' }, gap: 3 }}>
          <MonthlyChart
            title="Unique hectares verified, by month"
            subtitle="Each month counts its own plots once; months do not add up to the year when a plot is checked in several."
            data={usage.monthly.map((m) => ({ month: m.month, value: m.uniqueHectares }))}
            format={(v) => ha(v, 0)}
          />
          <MonthlyChart
            title="Active suppliers, by month"
            subtitle="Suppliers whose logins used the portal that month."
            data={usage.monthly.map((m) => ({ month: m.month, value: m.activeSupplierCompanies }))}
            format={(v) => num(v)}
          />
        </Box>
      )}
      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 4 }}>
        {formatDate(period.from)} to {formatDate(new Date(new Date(period.to).getTime() - 1).toISOString())}.
        Billed figures come from records that are never changed, so deleting data later does not alter them.
      </Typography>
    </>
  )
}

/** A billed dimension: the figure, and its allowance as a meter when one is set. */
function Tile({ label, value, note, allowance, pct, unit }: {
  label: string; value: string; note: string; allowance: number | null; pct: number | null; unit: string
}) {
  const over = pct != null && pct > 100
  return (
    <Surface sx={{ px: 5, py: 4 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{label}</Typography>
      <Typography sx={{ font: `700 30px ${tokens.font.display}`, color: tokens.color.brandDeep, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {allowance != null && pct != null ? (
        <Box sx={{ mt: 3 }}>
          <Box
            role="meter" aria-label={`${label} against allowance`} aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={Math.min(100, pct)} aria-valuetext={`${pct}% of ${num(allowance)}${unit}`}
            sx={{ height: 8, borderRadius: `${tokens.radius.pill}px`, backgroundColor: tokens.color.ink100, overflow: 'hidden' }}
          >
            <Box sx={{
              height: '100%', width: `${Math.min(100, pct)}%`, borderRadius: `${tokens.radius.pill}px`,
              backgroundColor: over ? tokens.color.warning : tokens.color.brandDeep,
            }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            {over && <CircleAlert size={14} color={tokens.color.warning} aria-hidden />}
            <Typography variant="caption" sx={{ color: over ? tokens.color.warning : 'text.secondary', fontWeight: over ? 600 : 400 }}>
              {over ? 'Over allowance: ' : ''}{pct}% of {num(allowance)}{unit}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 3 }}>No allowance set</Typography>
      )}
      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>{note}</Typography>
    </Surface>
  )
}

function Section({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <Surface sx={{ px: 5, py: 4 }}>
      <Eyebrow sx={{ mb: 2 }}>{title}</Eyebrow>
      <Box component="dl" sx={{ m: 0 }}>
        {rows.map(([k, v], i) => (
          <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, py: 1.25, borderTop: i ? `1px solid ${tokens.color.ink200}` : 0 }}>
            <Typography component="dt" variant="body2" sx={{ color: 'text.secondary' }}>{k}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{v}</Typography>
          </Box>
        ))}
      </Box>
    </Surface>
  )
}

// ---------------------------------------------------------------- monthly chart

const CHART_H = 160
const PAD = { top: 18, right: 8, bottom: 22, left: 44 }

/** Clean tick values: 0, a round step, up to just past the maximum. */
function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 1]
  const raw = max / 3
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw
  const ticks = [0]
  while (ticks[ticks.length - 1]! < max) ticks.push(ticks[ticks.length - 1]! + step)
  return ticks
}

const compact = (v: number) => (v >= 1000 ? `${(v / 1000).toLocaleString('en-GB', { maximumFractionDigits: 1 })}K` : v.toLocaleString('en-GB'))
const monthLabel = (m: string) => new Date(`${m}-01T00:00:00Z`).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })

/**
 * One series, one hue: columns per month from a single baseline, the
 * highest labelled, every month on hover, and a table view for the rest.
 */
function MonthlyChart({ title, subtitle, data, format }: {
  title: string; subtitle: string; data: { month: string; value: number }[]; format: (v: number) => string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const [table, setTable] = useState(false)
  const width = 560
  const innerW = width - PAD.left - PAD.right
  const innerH = CHART_H - PAD.top - PAD.bottom
  const ticks = niceTicks(Math.max(...data.map((d) => d.value)))
  const top = ticks[ticks.length - 1]!
  const band = innerW / data.length
  const barW = Math.min(24, band * 0.6)
  const y = (v: number) => PAD.top + innerH - (v / top) * innerH
  const peak = data.reduce((best, d, i) => (d.value > (data[best]?.value ?? -1) ? i : best), 0)

  return (
    <Surface sx={{ px: 5, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ font: `600 14px ${tokens.font.sans}` }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
        </Box>
        <Button size="small" variant="text" onClick={() => setTable((t) => !t)}>{table ? 'Chart' : 'Table'}</Button>
      </Box>
      {table ? (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 2, font: `400 13px ${tokens.font.sans}` }}>
          <thead><tr><Box component="th" sx={{ textAlign: 'left', color: 'text.secondary', fontWeight: 500, py: 1 }}>Month</Box><Box component="th" sx={{ textAlign: 'right', color: 'text.secondary', fontWeight: 500 }}>Value</Box></tr></thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.month}>
                <Box component="td" sx={{ py: 0.75, borderTop: `1px solid ${tokens.color.ink200}` }}>{d.month}</Box>
                <Box component="td" sx={{ textAlign: 'right', borderTop: `1px solid ${tokens.color.ink200}`, fontVariantNumeric: 'tabular-nums' }}>{format(d.value)}</Box>
              </tr>
            ))}
          </tbody>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', mt: 2 }}>
          <svg viewBox={`0 0 ${width} ${CHART_H}`} width="100%" role="img" aria-label={title} style={{ display: 'block' }}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke={tokens.color.ink200} strokeWidth={1} />
                <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill={tokens.color.ink500} fontFamily={tokens.font.sans}>{compact(t)}</text>
              </g>
            ))}
            {data.map((d, i) => {
              const cx = PAD.left + band * i + band / 2
              const h = Math.max(0, PAD.top + innerH - y(d.value))
              const x = cx - barW / 2
              const r = Math.min(4, h, barW / 2)
              const base = PAD.top + innerH
              const path = h > 0
                ? `M${x},${base} V${base - h + r} Q${x},${base - h} ${x + r},${base - h} H${x + barW - r} Q${x + barW},${base - h} ${x + barW},${base - h + r} V${base} Z`
                : ''
              return (
                <g key={d.month}>
                  {path && <path d={path} fill={tokens.color.brandDeep} opacity={hover === null || hover === i ? 1 : 0.55} />}
                  {i === peak && d.value > 0 && (
                    <text x={cx} y={base - h - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill={tokens.color.ink700} fontFamily={tokens.font.sans}>
                      {format(d.value)}
                    </text>
                  )}
                  <text x={cx} y={CHART_H - 6} textAnchor="middle" fontSize={11} fill={tokens.color.ink500} fontFamily={tokens.font.sans}>{monthLabel(d.month)}</text>
                  <rect
                    x={PAD.left + band * i} y={PAD.top} width={band} height={innerH} fill="transparent"
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                  />
                </g>
              )
            })}
          </svg>
          {hover !== null && data[hover] && (
            <Box sx={{
              position: 'absolute', pointerEvents: 'none', top: 0,
              left: `${((PAD.left + band * hover + band / 2) / width) * 100}%`, transform: 'translateX(-50%)',
              backgroundColor: tokens.color.ink900, color: tokens.color.ink0, borderRadius: `${tokens.radius.sm}px`,
              px: 1.5, py: 0.75, font: `500 12px ${tokens.font.sans}`, whiteSpace: 'nowrap',
            }}>
              {data[hover].month} · {format(data[hover].value)}
            </Box>
          )}
        </Box>
      )}
    </Surface>
  )
}

// ---------------------------------------------------------------- subscription

function SubscriptionDialog({ usage, onClose }: { usage: BillingUsage; onClose: () => void }) {
  const save = useSetSubscription(usage.organisationId)
  const s = usage.subscription ?? {}
  const [tier, setTier] = useState(s.tier ?? '')
  const [startsOn, setStartsOn] = useState(s.startsOn ?? '')
  const [suppliers, setSuppliers] = useState(s.suppliersAllowance?.toString() ?? '')
  const [hectares, setHectares] = useState(s.hectaresAllowance?.toString() ?? '')
  const [seats, setSeats] = useState(s.seatsAllowance?.toString() ?? '')
  const n = (v: string) => (v.trim() ? Number(v) : null)
  const submit = () => {
    const body: Subscription = {
      tier: tier.trim() || null, startsOn: startsOn || null,
      suppliersAllowance: n(suppliers), hectaresAllowance: n(hectares), seatsAllowance: n(seats),
    }
    save.mutate(body, { onSuccess: onClose })
  }
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Subscription: {usage.organisation}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: '8px !important' }}>
        <TextField label="Tier" value={tier} onChange={(e) => setTier(e.target.value)} placeholder="e.g. Petcare Professional" />
        <TextField label="Subscription year starts" type="date" value={startsOn} InputLabelProps={{ shrink: true }}
          onChange={(e) => setStartsOn(e.target.value)} helperText="Its month and day repeat each year. Empty: the calendar year." />
        <TextField label="Suppliers allowance" type="number" value={suppliers} onChange={(e) => setSuppliers(e.target.value)} />
        <TextField label="Unique hectares allowance, per year" type="number" value={hectares} onChange={(e) => setHectares(e.target.value)} />
        <TextField label="Seats allowance" type="number" value={seats} onChange={(e) => setSeats(e.target.value)} />
        {save.isError && <Typography variant="body2" sx={{ color: tokens.color.danger }}>{(save.error as Error).message}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={save.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------- bits

function HeaderCell({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', height: 36, justifyContent: right ? 'flex-end' : 'flex-start',
      font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled',
    }}>
      {children}
    </Box>
  )
}

function Cell({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <Box sx={{
      minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: right ? 'flex-end' : 'flex-start',
      borderTop: `1px solid ${tokens.color.ink200}`, minWidth: 0,
    }}>
      {children}
    </Box>
  )
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
